import { motion } from 'framer-motion';
import React, { useCallback, useEffect } from 'react';
import { FiCamera } from 'react-icons/fi';

import bgUno from '@/assets/tournaments/bg_thumb-1.jpg';
import bgDos from '@/assets/tournaments/bg_thumb-2.jpg';
import bgTres from '@/assets/tournaments/bg_thumb-3.jpg';
import bgCuatro from '@/assets/tournaments/bg_thumb-4.jpg';
import bgCinco from '@/assets/tournaments/bg_thumb-5.jpg';
import bgSeis from '@/assets/tournaments/bg_thumb-6.jpg';
import { Button, Card, Modal } from '@/components';
import { ImageCropModal } from '@/components/ReactEasyCrop/ImageCropModal';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import { selectPredefinedCoverImage, uploadCoverImage } from '@/services/tda';
import { ImageCover } from '@/store';
import { COLORS, FADE_IN_OUT } from '@/utils';

export const THUMBS: ImageCover[] = [
  { id: 1, src: bgUno },
  { id: 2, src: bgDos },
  { id: 3, src: bgTres },
  { id: 4, src: bgCuatro },
  { id: 5, src: bgCinco },
  { id: 6, src: bgSeis },
  { id: 7, src: '', color: COLORS['gdt-blue'].header },
  { id: 8, src: '', color: COLORS['gdt-blue'].DEFAULT },
  { id: 9, src: '', color: COLORS['gdt-blue'].light },
];

export const COVER: ImageCover[] = [
  { id: 1, src: '/assets/images/bg_cover-1.jpg' },
  { id: 2, src: '/assets/images/bg_cover-2.jpg' },
  { id: 3, src: '/assets/images/bg_cover-3.jpg' },
  { id: 4, src: '/assets/images/bg_cover-4.jpg' },
  { id: 5, src: '/assets/images/bg_cover-5.jpg' },
  { id: 6, src: '/assets/images/bg_cover-6.jpg' },
  { id: 7, src: '', color: COLORS['gdt-blue'].header },
  { id: 8, src: '', color: COLORS['gdt-blue'].DEFAULT },
  { id: 9, src: '', color: COLORS['gdt-blue'].light },
];

type PropsModalCover = {
  onHandleThumbsSelected: (image: ImageCover) => void;
  onHandleLoadImage: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const ModalCoverSelector: React.FC<PropsModalCover> = React.memo(
  ({ onHandleLoadImage, onHandleThumbsSelected }) => {
    const { tourneyData, handleSetTourney } = useTourney();
    const toast = useToast();
    return (
      <Modal
        showCloseButton
        className="!max-w-[449px] px-6"
        controlledOnClose={() =>
          handleSetTourney({
            ...tourneyData,
            showModalCover: false,
          })
        }
        isControlledOpen={Boolean(tourneyData.showModalCover)}
        render={() => (
          <div className="text-center mt-9">
            <div className="grid grid-cols-3 gap-3">
              {THUMBS.map((img) => (
                <figure
                  key={img.id}
                  className="w-32 h-[89px] rounded-lg cursor-pointer"
                  style={{ backgroundColor: img.color }}
                  onClick={() => onHandleThumbsSelected(img)}
                >
                  {img.src && <img alt={img.src} src={img.src} />}
                </figure>
              ))}
            </div>
            <Button
              className="mt-9 relative w-52 h-[48px]"
              type="button"
              variant="primary"
            >
              <input
                accept="image/gif, image/jpg, image/png, image/JPEG, image/JPG"
                className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                type="file"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (!event.target.files) return;
                  if (
                    !['png', 'jpg', 'jpeg', 'gif'].includes(
                      event.target.files[0].type
                        .split('/')[1]
                        .toLocaleLowerCase()
                    )
                  ) {
                    toast.show(
                      'El archivo que subas debe ser .gif, .jpg o .png. Volvé a cargar una imagen.'
                    );
                  } else if (event.target.files[0].size > 500000) {
                    toast.show(
                      'El archivo que subas no debe pesar más de 500kb. Volvé a cargar una imagen.'
                    );
                  } else {
                    onHandleLoadImage(event);
                  }
                }}
              />
              Cargar una imagen
            </Button>
          </div>
        )}
        size="xs"
        title="Elegí una portada"
        variant="controlled"
      />
    );
  }
);

const ModalImageCropper: React.FC<{
  onSubmitImageCover: (imageSrc: Blob | null) => void;
}> = React.memo(({ onSubmitImageCover }) => {
  const { tourneyData, handleSetTourney } = useTourney();
  return (
    <Modal
      showCloseButton
      className="!min-w-[1100px] px-6"
      controlledOnClose={() =>
        handleSetTourney({
          ...tourneyData,
          selectedCoverFromFile: undefined,
          showImageModalFromFile: false,
        })
      }
      isControlledOpen={Boolean(tourneyData.showImageModalFromFile)}
      render={() =>
        tourneyData.selectedCoverFromFile && (
          <ImageCropModal
            target={tourneyData.selectedCoverFromFile}
            onSubmit={onSubmitImageCover}
          />
        )
      }
      title="Podés hacer zoom con la rueda del mouse y/o arrastrar la foto"
      variant="controlled"
    />
  );
});

export const CoverImage: React.FC = React.memo(() => {
  const { tourneyData, handleSetTourney } = useTourney();
  const toast = useToast();

  useEffect(() => {
    setTimeout(() => {
      handleSetTourney({
        ...tourneyData,
        selectedCoverDefault: COVER.find(
          (e) => e.id === tourneyData.selectedCover?.id
        ),
        selectedSourceImageCover: tourneyData.selectedCover?.url,
      });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.selectedCover]);

  // persistencia de imagen de portada
  const onSubmitImageCover = useCallback(
    async (params: Blob | null) => {
      // TODO: por si piden eliminar en algun momento portada
      // deleteCoverImage(idTda);
      if (params !== null) {
        try {
          const response = await uploadCoverImage(params, tourneyData.id);
          if (response.estado && response.portada.pathImagen) {
            setTimeout(() => {
              handleSetTourney({
                ...tourneyData,
                selectedSourceImageCover: response.portada.pathImagen,
                selectedCover: { url: response.portada.pathImagen },
                showImageModalFromFile: false,
              });
            }, 0);
          } else {
            console.error(response.mensaje);
            toast.show(response.mensaje);
          }
        } catch (error) {
          console.error(error);
        }
      }

      handleSetTourney({
        ...tourneyData,
        showImageModalFromFile: false,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tourneyData]
  );

  // persistencia de portadas por defecto
  const onHandleThumbsSelected = async (image: ImageCover) => {
    try {
      const response = await selectPredefinedCoverImage(
        tourneyData.id,
        image.id
      );
      if (response.estado && response.portada.idPortadaPredefinida) {
        handleSetTourney({
          ...tourneyData,
          showModalCover: false,
          selectedCoverDefault: COVER.find(
            (e) => e.id === response.portada.idPortadaPredefinida
          ),
          selectedCover: { id: response.portada.idPortadaPredefinida },
        });
      } else {
        console.error(response.mensaje);
        toast.show(response.mensaje);
      }
    } catch (error) {
      console.error(error, error);
    }
  };

  const onHandleLoadImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target;
    handleSetTourney({
      ...tourneyData,
      showModalCover: false,
      showImageModalFromFile: true,
      selectedCoverFromFile: selectedFile,
    });
  };

  const onHandleModalSelector = () => {
    handleSetTourney({
      ...tourneyData,
      showModalCover: true,
    });
  };

  // muestro portada para dueño y no dueño
  if (
    (tourneyData.tourney?.portada &&
      Object.values(tourneyData.tourney?.portada).length !== 0) ||
    tourneyData.selectedCover?.id ||
    tourneyData.selectedCover?.url ||
    !tourneyData.isOwner
  ) {
    return (
      <motion.div animate={'show'} initial="hide" variants={FADE_IN_OUT}>
        <Card
          className="flex bg-white justify-center items-center w-full h-40 p-0 relative"
          style={{ backgroundColor: tourneyData.selectedCoverDefault?.color }}
        >
          {((tourneyData.selectedCoverDefault &&
            tourneyData.selectedCoverDefault?.src) ||
            tourneyData?.selectedSourceImageCover) && (
            <figure className="overflow-hidden rounded-lg w-full">
              <img
                alt="Imagen de portada de Torneo de Amigos"
                className="w-full h-[160px]"
                src={
                  tourneyData.selectedCoverDefault?.src ||
                  tourneyData.selectedSourceImageCover
                }
              />
            </figure>
          )}
          {tourneyData.isOwner && (
            <figure
              className="w-8 h-8 bg-gdt-blue rounded-full flex items-center justify-center absolute -bottom-3 right-4 cursor-pointer overflow-hidden"
              onClick={onHandleModalSelector}
            >
              <FiCamera color="white" size={17} />
            </figure>
          )}
        </Card>
        <ModalCoverSelector
          onHandleLoadImage={onHandleLoadImage}
          onHandleThumbsSelected={onHandleThumbsSelected}
        />
        <ModalImageCropper onSubmitImageCover={onSubmitImageCover} />
      </motion.div>
    );
  }

  // muestro portada vacía con acciones de modal
  if (
    (Boolean(!tourneyData.tourney?.portada) && tourneyData.isOwner) ||
    (tourneyData.isOwner &&
      tourneyData.tourney?.portada &&
      Object.values(tourneyData.tourney?.portada).length === 0)
  ) {
    return (
      <motion.div animate={'show'} initial="hide" variants={FADE_IN_OUT}>
        <Card
          className="flex bg-white justify-center items-center w-full cursor-pointer h-40"
          handleClick={onHandleModalSelector}
        >
          <div className="flex  items-center space-x-3">
            <figure className="w-8 h-8 bg-gdt-blue rounded-full flex items-center justify-center">
              <FiCamera color="white" size={17} />
            </figure>
            <p className="text-gdt-blue text-base font-bold">
              Editar imagen de portada
            </p>
          </div>
        </Card>
        {
          <ModalCoverSelector
            onHandleLoadImage={onHandleLoadImage}
            onHandleThumbsSelected={onHandleThumbsSelected}
          />
        }
        {<ModalImageCropper onSubmitImageCover={onSubmitImageCover} />}
      </motion.div>
    );
  }
});

CoverImage.displayName = 'CoverImage';
ModalCoverSelector.displayName = 'ModalSelector';
ModalImageCropper.displayName = 'ModalImageCropper';
